import { Fragment, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { MdContentCopy } from 'react-icons/md';
import { toast } from 'react-hot-toast';
import EditFundraisingModal from '../modals/EditFundraisingModal';
import ManageNxtFundModal from '../modals/ManageNxtFundModal';

interface Props {
  csc?: any;
  campaign?: any;
  campaignRequest?: any;
  patient?: any;
  onDataUpdate?: () => void;
}

const CampaignTab: React.FC<Props> = ({
  csc,
  campaign,
  campaignRequest,
  patient,
  onDataUpdate,
}) => {
  const [isFundraisingModalOpen, setIsFundraisingModalOpen] = useState(false);
  const [isFundModalOpen, setIsFundModalOpen] = useState(false);

  const formatThermStartDate = (raw: string | null | undefined) => {
    if (!raw) return '';
    try {
      return format(parseISO(raw), 'M/d/yyyy');
    } catch {
      return raw;
    }
  };

  return (
    <>
      <div className='p-[12px] pt-0'>
        <h2 className='text-[22px]/[28px] font-bold'>Campaign</h2>
        <div className='flex justify-between'>
          <div className='grow'>
            <div className='mb-[16px]'>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Client Services Coordinator
              </label>
              <div className={csc ? '' : 'italic text-[#949599]'}>
                {csc
                  ? `${csc.first_name} ${csc.last_name}`
                  : 'None Assigned'}
              </div>
            </div>
            <div className='mb-[16px]'>
              <label className='text-[12px]/[16px] text-[#949599]'>
                NTAF ID
              </label>
              <div className='flex items-center gap-2'>
                <button
                  type='button'
                  onClick={() => setIsFundModalOpen(true)}
                  className='text-left hover:underline'
                  title={
                    campaign?.nxt_fund_id
                      ? 'View NXT fund details'
                      : 'Link or create an NXT fund'
                  }
                >
                  {campaign?.ntaf_id || (
                    <span className='italic text-[#949599]'>None</span>
                  )}
                </button>
                {!campaign?.nxt_fund_id && (
                  <span className='text-[12px]/[16px] text-[#F02D3A]'>
                    (no fund linked)
                  </span>
                )}
              </div>
            </div>
            <div className='mb-[16px]'>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Fund Name
              </label>
              <div className='flex items-center gap-2'>
                <span>{campaign?.fund_name}</span>
                {campaign?.fund_name && (
                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(campaign.fund_name);
                      toast.success('Fund name copied to clipboard');
                    }}
                    className='p-1 rounded transition-colors'
                    style={{ color: '#00AEB3' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#E6F7F8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title='Copy fund name'
                  >
                    <MdContentCopy size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className='mb-[16px]'>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Region
              </label>
              <div>{campaign?.region}</div>
            </div>
            <div>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Visibility
              </label>
              <div>{campaign?.privacy_flag ? 'Private' : 'Public'}</div>
            </div>
          </div>
          <div className='grow'>
            <div>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Campaign Status
              </label>
              <div className='grid grid-cols-[max-content_max-content] items-baseline gap-x-4'>
                {[
                  { label: 'Unpublished', date: campaign?.unpublished_date },
                  { label: 'Pending Financial Need', date: campaign?.pend_financial_need_date },
                  { label: 'Approved', date: campaign?.approved_date },
                  { label: 'Open', date: campaign?.funded_date },
                  { label: 'Disbursable', date: campaign?.disbursable_date },
                  { label: 'Closed', date: campaign?.closed_date },
                  { label: 'Reopened', date: campaign?.reopened_date },
                ].map(({ label, date }) => (
                  <Fragment key={label}>
                    <div className={!date ? 'text-[#949599]' : ''}>{label}</div>
                    {date ? (
                      <div className='text-sm italic'>
                        {format(parseISO(date), 'M/d/yyyy')}
                      </div>
                    ) : (
                      <div />
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        <hr className='my-[24px] border-stroke' />
        <div className='mb-[16px] flex items-center justify-between'>
          <h3 className='text-[18px]/[24px] font-bold'>Fundraising</h3>
          <button
            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
            onClick={() => setIsFundraisingModalOpen(true)}
          >
            Edit
          </button>
        </div>
        <div className='grid grid-cols-1 gap-[16px] sm:grid-cols-2 lg:grid-cols-4'>
          <div>
            <label className='text-[12px]/[16px] text-[#949599]'>
              Thermometer Start Date
            </label>
            <div>{formatThermStartDate(campaign?.therm_start_date)}</div>
          </div>
          <div>
            <label className='text-[12px]/[16px] text-[#949599]'>
              Fundraising Goal
            </label>
            <div>
              {parseInt(campaign?.goal || 0).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              })}
            </div>
          </div>
          <div>
            <label className='text-[12px]/[16px] text-[#949599]'>
              Since Thermometer Start
            </label>
            <div>
              {campaign?.therm_start_date
                ? parseInt(campaign?.total_funds_raised || 0).toLocaleString(
                    'en-US',
                    {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0,
                    }
                  )
                : ''}
            </div>
          </div>
          <div>
            <label className='text-[12px]/[16px] text-[#949599]'>
              Since Campaign Start
            </label>
            <div>
              {parseFloat(campaign?.entire_funds_raised || 0).toLocaleString(
                'en-US',
                {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }
              )}
            </div>
          </div>
        </div>
      </div>
      <EditFundraisingModal
        isOpen={isFundraisingModalOpen}
        onClose={() => setIsFundraisingModalOpen(false)}
        campaignId={campaign?.id || campaign?.campaign_id}
        initialThermStartDate={campaign?.therm_start_date}
        initialGoal={campaign?.goal}
        onSaved={() => onDataUpdate?.()}
      />
      <ManageNxtFundModal
        isOpen={isFundModalOpen}
        onClose={() => setIsFundModalOpen(false)}
        campaign={campaign}
        patient={patient}
        onLinked={() => onDataUpdate?.()}
      />
    </>
  );
};

export default CampaignTab;
